import { useCallback, useState, useEffect, memo } from 'react'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import CropRotateIcon from '@mui/icons-material/CropRotate'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../generic/Api_Functions'
import { Button, Stack, TextField, Typography, Box, Chip, Grid, Tooltip, LinearProgress, Divider } from '@mui/material'
import useMosInfo from '../common/provider/useMosInfo'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import JobStatusChangeDialog from './PlannedJobs/components/JobStatusChangeDialog'
import WorkerRequiredButton from '../common/WorkerRequiredButton'
import DefaultBeforeSelectText from '../common/DefaultBeforeSelectText'
import AlertUI from '../../../generic/AlertUI'
import ReportQuantityDialog from './ReportQuantityDialog'
import PauseIcon from '@mui/icons-material/Pause'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ActiveJobList from './ActiveMultiJob/components/ActiveJobList'
import BatchBadge from '../common/batch/BatchBadge'
import BatchActionsBar from './ActiveMultiJob/components/BatchActionsBar'
import BatchBlockedNotice from '../common/batch/BatchBlockedNotice'
import { buildBatchBadges } from '../common/batch/batchLabel'
import { groupByBatchId, useBatchActions } from '../common/batch/useBatchActions'
import useFrontEndConfig from '../../../generic/hooks/useFrontEndConfig'
import { getJobAutoReportParts, getJobBatchId, getJobExternalCircuit, getJobGovernedByAcceptance, getJobManagePreparation, getJobStatusCode, isJobInPreparation } from '../common/reportOutcome'
import { JOB_WIDGET_ACTION, resolveJobModeActions } from '../common/jobModeActions'
import useJobModeTransition from '../common/useJobModeTransition'
import JobModeChangeDialog from '../common/JobModeChangeDialog'
import PreparationLabel from '../common/PreparationLabel'
import BatchWarningDialog from '../common/batch/BatchWarningDialog'
import BatchFinishDialog from '../common/batch/BatchFinishDialog'
import { resolveBatchWarningKind, BATCH_WARNING_KIND } from '../common/batch/batchWarning'
import { JOB_ACTION_INTENT } from './PlannedJobs/utils/jobStatus'

// Historia 120 (Jupiter, paquete P11) -- S141/R-16: this widget (`ActiveFabricationOrderJobWidgetProvider`,
// code `ActiveFoJobListWidget`, "Active jobs") is a FIFTH, independently selectable Finish surface --
// the legacy compact station widget, alongside the Planned Jobs tabs and All Active Jobs (P6/P6b),
// the master-detail (P7/P9) and Active Single Job (P10). Wired here with the SAME resolver/dialogs
// those already use (`common/batch/batchWarning.js`, `BatchWarningDialog`, `BatchFinishDialog`),
// never a copy of the condition. No new route, no new field: `getJobBatchId` reads the SAME per-job
// flag `common/reportOutcome.js` already exposes to the other four surfaces.
const CLOSED_BATCH_WARNING = { open: false, kind: BATCH_WARNING_KIND.NONE, batchId: '' }
const CLOSED_BATCH_FINISH = { open: false, batchId: '' }

const InfoItem = memo(({ label, value, tooltip }) => (
  <Box sx={{ overflow: 'hidden' }}>
    <Typography variant='caption' color='text.secondary' display='block' noWrap>
      {label}
    </Typography>
    <Tooltip title={tooltip || value || ''} placement='top-start'>
      <Typography variant='body2' fontWeight='medium' noWrap>
        {value}
      </Typography>
    </Tooltip>
  </Box>
))

const ActiveFabricationOrderJobWidget = (props) => {
  const { t_ } = useSmartTranslation()
  const { fabricationOrder, node, jobReference, selectNode, unSelectNode, activeJobs, activeJobsProgress, globalData, device, worker, refreshActiveJobs } = useMosInfo()
  const showJobsList = globalData?.showActiveJobsList === true
  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false })
  // S124: the two-action mode modal behind "Change status".
  const [modeChangeOpen, setModeChangeOpen] = useState(false)
  const [reportQuantityDialog, setReportQuantityDialog] = useState({ open: false })
  const [quantity, setQuantity] = useState('')
  const [jobProgress, setJobProgress] = useState(null)
  // S141 (P11): declared here, alongside the other useState calls -- this component has no early
  // return above them (§7.2 #9: every hook already sits above the widget's only conditional return,
  // at the bottom of this function).
  const [batchWarning, setBatchWarning] = useState(CLOSED_BATCH_WARNING)
  const [batchFinish, setBatchFinish] = useState(CLOSED_BATCH_FINISH)
  const [AlertElement, showAlert] = AlertUI()

  const loadInfo = useCallback(async () => {
    if (!fabricationOrder?.id || !jobReference?.id) {
      return null
    }
    try {
      const response = await apiGet(`mos/fabricationOrder/${fabricationOrder.id}/progress`)
      const job = response?.jobs?.find(job => job.id === jobReference.id)
      const canFinish = job?.canFinish ?? false
      return job
        ? {
            available: job.available ?? 0,
            completed: job.completed_quantity ?? 0,
            planned: job.quantity ?? 0,
            done: job.done ?? 0,
            fail: job.fail ?? 0,
            total: response.total ?? 0,
            canFinish,
            isFirst: job.first ?? false
          }
        : null
    } catch (error) {
      console.error(error)
      return null
    }
  }, [fabricationOrder?.id, jobReference?.id])

  useSmartInterval(loadInfo, 5000, setJobProgress)

  useEffect(() => {
    setQuantity('')
    setJobProgress(null)
    loadInfo().then(setJobProgress)
  }, [fabricationOrder?.id, jobReference?.id, loadInfo])

  const changeStatus = useCallback((status) => {
    setChangeStatusDialog({ open: true, jobReference, fabricationOrder, statusCode: status, node })
  }, [jobReference, fabricationOrder, node])

  // S141/R-16 (P11) -- THE derivation point for this widget's Finish button: the SAME resolver
  // P6/P7/P10 already use (`resolveBatchWarningKind`), fed the SAME per-job flag (`getJobBatchId`). A
  // job with no batch (absent/null/blank) resolves NONE and falls straight into today's individual
  // close -- no behaviour change for the vast majority of jobs, which never belonged to a batch.
  const handleFinishClick = useCallback(() => {
    const batchId = getJobBatchId(activeJobs, fabricationOrder, jobReference)
    const kind = resolveBatchWarningKind({ batchId }, JOB_ACTION_INTENT.FINISH)
    if (kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setBatchWarning({ open: true, kind, batchId })
      return
    }
    changeStatus('FINISHED')
  }, [activeJobs, fabricationOrder, jobReference, changeStatus])

  // S129/S134/S136-equivalent for this widget: dismissing the warning fires neither choice.
  const handleBatchWarningClose = () => setBatchWarning(CLOSED_BATCH_WARNING)

  // "Finish only this job" -- today's individual close, unchanged, no batch call of any kind.
  const handleBatchWarningChooseIndividual = () => {
    setBatchWarning(CLOSED_BATCH_WARNING)
    changeStatus('FINISHED')
  }

  // "Finish the whole batch" -- N4+N5 via `BatchFinishDialog` (P8), mounted as-is, no new route.
  const handleBatchWarningChooseBatch = () => {
    const { batchId } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)
    setBatchFinish({ open: true, batchId })
  }

  const handleBatchFinishClose = () => setBatchFinish(CLOSED_BATCH_FINISH)

  const handleBatchFinished = () => {
    setBatchFinish(CLOSED_BATCH_FINISH)
    unSelectNode()
    refreshActiveJobs()
  }

  const [frontEndConfig] = useFrontEndConfig()
  const allowGreaterThanFo = frontEndConfig?.inputGreaterAmountThanTheFo !== false
  const isFirstJob = jobProgress?.isFirst ?? false
  const maxQuantity = jobProgress?.available ?? 0
  // When INPUT_GREATER_AMOUNT_THAN_THE_OF is disabled, the first job is also capped to the
  // remaining FO quantity (planned - done - fail). Otherwise it is unbounded (scrap replacement).
  const firstJobRemaining = jobProgress
    ? Math.max((jobProgress.planned ?? 0) - (jobProgress.done ?? 0) - (jobProgress.fail ?? 0), 0)
    : 0
  const reportMax = isFirstJob
    ? (allowGreaterThanFo ? Infinity : firstJobRemaining)
    : maxQuantity
  // S51 (CA11/RN24/D18, story 86): D18 explicitly includes this compact legacy widget -- a job
  // governed by the acceptance form is observable but not operable here either. Computed
  // independently from the single/multi containers (this widget does not share their calculation).
  // S239/S245 (ENMIENDA 08, G3-12 b) -- los distintivos de lote de ESTE widget salen de la MISMA
  // agrupación que el maestro-detalle, sobre los MISMOS `activeJobs` (F3): mismo lote, mismo color en
  // los tres widgets. Ninguna petición nueva: el `batchId` ya viaja por job.
  // 🔴 S248 (ENMIENDA 09, G3-13/R-16) -- ESTE WIDGET MONTA LAS PIEZAS QUE YA EXISTEN: el hook de
  // acciones de lote, la barra, el aviso de bloqueo y el diálogo de cierre. NO evalúa una condición
  // propia: un `if (batchId)` copiado en cada widget es el defecto, porque tres copias de la misma
  // regla divergen en la primera corrección — que es justo el origen de las tres superficies de S141.
  const { batchGroups: batchActionGroups, selectedBatchGroup, pauseBatchGroup, blockedPause, closeBlockedPause } = useBatchActions({
    activeItems: activeJobs,
    device,
    worker,
    selectedJob: { fabricationOrderId: fabricationOrder?.id, jobReferenceId: jobReference?.id },
    refreshActiveJobs,
    showAlert,
    t_
  })

  const batchBadges = buildBatchBadges(groupByBatchId(activeJobs, t_), t_)
  const shownJobBatchBadge = batchBadges[getJobBatchId(activeJobs, fabricationOrder, jobReference)] ?? null

  const governedByAcceptance = getJobGovernedByAcceptance(activeJobs, fabricationOrder, jobReference)
  // S79 (story 100, RN34/C9(1)): a JOB that crossed the external circuit gate is a DIFFERENT domain
  // concept, computed independently here too -- gates the SAME two controls as S51, PLUS Report
  // (this widget's own quantity/Report button), which #86 never touched.
  const externalCircuitJob = getJobExternalCircuit(activeJobs, fabricationOrder, jobReference)
  const externalCircuitJobTooltip = externalCircuitJob
    ? t_('External job: actions are governed by the external jobs widget')
    : ''
  // Story 113 (S134): the job in preparation is one more reason to gate this widget's own quantity
  // control, on top of the ones it already had.
  const inPreparation = isJobInPreparation(activeJobs, fabricationOrder, jobReference) &&
    getJobManagePreparation(activeJobs, fabricationOrder, jobReference)
  const reportDisabled = !jobProgress || Number(quantity) <= 0 ||
    (reportMax !== Infinity && (!reportMax || Number(quantity) > reportMax)) || externalCircuitJob ||
    inPreparation
  // S134/S139: the reason for the report gate. A PRE-EXISTING gate keeps its own explanation --
  // preparation never steals it. Empty when the control is not blocked by either, so the widget
  // renders no tooltip on an enabled control (its quantity gates carry no message of their own,
  // exactly as before the story).
  const reportDisabledTooltip = externalCircuitJobTooltip ||
    (inPreparation ? t_('Reporting is only available in execution') : '')
  const finishDisabled = !jobProgress?.canFinish
  // When the job's technology auto-reports parts, clicking Finish makes Sun report the remaining
  // quantity automatically and close the job, so Finish must not be blocked at all — neither by the
  // available quantity nor by canFinish (which stays false precisely because those parts are not yet
  // reported; requiring it here would deadlock the auto-report flow).
  const autoReportParts = getJobAutoReportParts(activeJobs, fabricationOrder, jobReference)
  const progressPercent = jobProgress?.total ? Math.min(100, Math.round((jobProgress.done / jobProgress.total) * 100)) : 0
  // Story 113 (S120/S133, R-G): this widget kept its OWN copy of the layout rule -- the fourth one.
  // The two buttons below now render the descriptors the shared point resolves; the gates it already
  // computed feed into it unchanged.
  const gatedTooltip = governedByAcceptance
    ? t_('Acceptance job: actions are governed by the acceptance form')
    : externalCircuitJobTooltip
  // Resolved BEFORE the layout: `busy` feeds the Go to execution button's disabled state.
  const { goToExecution, backToPreparation, busy: modeTransitionBusy } = useJobModeTransition({
    device,
    fabricationOrder,
    jobReference,
    worker,
    activeJobs,
    refreshActiveJobs,
    showAlert,
    t_
  })
  const modeActions = resolveJobModeActions({
    managePreparation: getJobManagePreparation(activeJobs, fabricationOrder, jobReference),
    statusCode: getJobStatusCode(activeJobs, fabricationOrder, jobReference),
    finishDisabled: governedByAcceptance || externalCircuitJob ||
      (!autoReportParts && ((maxQuantity === null || maxQuantity !== 0) || finishDisabled)),
    pauseDisabled: governedByAcceptance || externalCircuitJob,
    gatedTooltip,
    finishDisabledTooltip: finishDisabled ? t_('Finish disabled for this job') : '',
    modeTransitionBusy
  })
  const handleModeAction = {
    [JOB_WIDGET_ACTION.PAUSE]: () => changeStatus('INTERRUPTED'),
    [JOB_WIDGET_ACTION.FINISH]: handleFinishClick,
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: goToExecution,
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: () => setModeChangeOpen(true)
  }
  const MODE_ACTION_ICON = {
    [JOB_WIDGET_ACTION.PAUSE]: <PauseIcon />,
    [JOB_WIDGET_ACTION.FINISH]: <CheckCircleIcon />,
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: <PlayArrowIcon />,
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: <SwapHorizIcon />
  }
  const MODE_ACTION_STYLE = {
    [JOB_WIDGET_ACTION.PAUSE]: { variant: 'outlined', color: 'warning' },
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: { variant: 'outlined', color: 'warning' },
    [JOB_WIDGET_ACTION.FINISH]: { variant: 'contained', color: 'success' },
    // S122: it takes the finish button's position, so it must NOT look like the disabled one.
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: { variant: 'contained', color: 'primary' }
  }
  const renderModeButton = (descriptor) => (
    <Tooltip title={descriptor.tooltip} placement='top' arrow>
      <Box>
        <WorkerRequiredButton
          variant={MODE_ACTION_STYLE[descriptor.action].variant}
          color={MODE_ACTION_STYLE[descriptor.action].color}
          fullWidth
          size='medium'
          startIcon={MODE_ACTION_ICON[descriptor.action]}
          onClick={handleModeAction[descriptor.action]}
          disabled={descriptor.disabled}
          sx={{ height: 48 }}
        >
          {t_(descriptor.label)}
        </WorkerRequiredButton>
      </Box>
    </Tooltip>
  )

  if (fabricationOrder && jobReference && !showJobsList) {
    return (
      <>
        {AlertElement}
        {/* S124-S126: exactly two actions behind "Change status". */}
        <JobModeChangeDialog
          open={modeChangeOpen}
          onClose={() => setModeChangeOpen(false)}
          onBackToPreparation={() => { setModeChangeOpen(false); backToPreparation() }}
          onPauseWork={() => { setModeChangeOpen(false); changeStatus('INTERRUPTED') }}
        />
        <JobStatusChangeDialog
          open={changeStatusDialog.open}
          onCancell={() => setChangeStatusDialog({ ...changeStatusDialog, open: false })}
          node={changeStatusDialog.node}
          jobReference={changeStatusDialog.jobReference}
          fabricationOrder={changeStatusDialog.fabricationOrder}
          statusCode={changeStatusDialog.statusCode}
        />
        <ReportQuantityDialog
          open={reportQuantityDialog.open}
          onCancel={() => setReportQuantityDialog({ ...reportQuantityDialog, open: false })}
          onSuccess={() => {
            setReportQuantityDialog({ ...reportQuantityDialog, open: false })
            setQuantity('')
            loadInfo().then(setJobProgress)
          }}
          jobReference={jobReference}
          fabricationOrder={fabricationOrder}
          statusCode={reportQuantityDialog.statusCode}
          quantity={quantity}
        />

        {/* S141 (P11): the ONE warning, reused verbatim -- this widget only ever produces
            FINISH_BATCH_MEMBER (it has no Start/group-shortcut/resume gesture of its own). */}
        <BatchWarningDialog
          open={batchWarning.open}
          kind={batchWarning.kind}
          onClose={handleBatchWarningClose}
          onChooseIndividual={handleBatchWarningChooseIndividual}
          onChooseBatch={handleBatchWarningChooseBatch}
        />

        {/* S141 (P11): "finish the whole batch" -- N4+N5 as-is, no new route (P8's dialog, unmodified). */}
        <BatchFinishDialog
          open={batchFinish.open}
          deviceId={device?.id}
          workerId={worker?.id}
          batchId={batchFinish.batchId}
          onClose={handleBatchFinishClose}
          onFinished={handleBatchFinished}
        />
        <Stack sx={{ height: '100%', p: 1, overflow: 'hidden', gap: 1 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant='subtitle1' fontWeight='bold' noWrap>
                {t_('Active job')}
              </Typography>
              {/* S141/RN51: the label sits beside the FO name. D-I6: only preparation is labelled. */}
              <Stack direction='row' spacing={0.75} alignItems='center' sx={{ minWidth: 0 }}>
                {/* S245: junto al título, para saber de qué lote es el job mostrado SIN desplegar la
                    lista. Mismo componente y mismo color que el de la fila (S244). */}
                <BatchBadge badge={shownJobBatchBadge} />
                <Chip label={fabricationOrder.fabricationOrder} color='primary' size='small' sx={{ height: 24 }} />
                <PreparationLabel visible={inPreparation} />
              </Stack>
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <InfoItem label={t_('FO')} value={fabricationOrder.fabricationOrder} />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label={t_('Part')} value={fabricationOrder.partReference.code} />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label={t_('Job')} value={node.name} />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label={t_('Tech')} value={jobReference?.technology?.code ?? ''} />
              </Grid>
            </Grid>

            {jobProgress && (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant='caption' color='text.secondary'>{t_('Progress')}</Typography>
                  <Typography variant='caption' fontWeight='bold'>{jobProgress.done} / {jobProgress.total}</Typography>
                </Box>
                <LinearProgress variant='determinate' value={progressPercent} sx={{ height: 6, borderRadius: 3 }} />
              </Box>
            )}

            <Divider sx={{ my: 0.5 }} />

            <Box>
              <Grid container spacing={1} alignItems='flex-end'>
                <Grid item xs={8}>
                  <TextField
                    label={t_('Quantity')}
                    type='number'
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    disabled={!jobProgress || externalCircuitJob || inPreparation}
                    fullWidth
                    size='small'
                    placeholder={String(maxQuantity)}
                    inputProps={{
                      min: 0,
                      max: reportMax !== Infinity ? reportMax : undefined,
                      'aria-label': t_('Quantity')
                    }}
                    InputProps={{
                      endAdornment: <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', ml: 1 }}>/ {reportMax === Infinity ? '∞' : reportMax}</Typography>,
                      sx: { height: 48 }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  {/* S79 (story 100): the Tooltip wraps a Box because a disabled MUI button fires no
                      mouse events, same wrapping Pause/Finish below (S60/S62) already use. */}
                  <Tooltip title={reportDisabledTooltip} placement='top' arrow>
                    <Box>
                      <Button
                        variant='contained'
                        color='primary'
                        fullWidth
                        size='medium'
                        disabled={reportDisabled}
                        onClick={() => {
                          if (quantity === '' || quantity === null) {
                            showAlert({ severity: 'warning', message: t_('Report quantity cannot be empty') })
                          } else {
                            setReportQuantityDialog({ open: true, node, jobReference, fabricationOrder, statusCode: 'IN_PROGRESS' })
                          }
                        }}
                        sx={{ height: 48 }}
                      >
                        {t_('Report')}
                      </Button>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderModeButton(modeActions.left)}
            </Grid>
            <Grid item xs={6}>
              {renderModeButton(modeActions.right)}
            </Grid>
          </Grid>
        </Stack>

        {/* S246/S248: la pareja, en el PIE de la vista del job y con peso secundario -- la MISMA
            barra del maestro-detalle, con la etiqueta y el distintivo de su lote al lado. */}
        <BatchActionsBar
          group={selectedBatchGroup}
          badge={selectedBatchGroup ? batchBadges[selectedBatchGroup.batchId] : null}
          onPauseBatch={pauseBatchGroup}
          onOpenFinishBatch={(batchId) => setBatchFinish({ open: true, batchId })}
          t_={t_}
        />
        {/* S147: el MISMO renderizador del BLOCKED que usa el maestro-detalle, nunca un segundo. */}
        <BatchBlockedNotice open={blockedPause.open} jobs={blockedPause.jobs} onClose={closeBlockedPause} />
      </>
    )
  }

  if (activeJobs.length === 0) {
    return <DefaultBeforeSelectText />
  }

  return (
    <Box sx={{ height: '100%', p: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <ActiveJobList
        items={activeJobs}
        selectedFabricationOrder={fabricationOrder}
        selectedJobReference={jobReference}
        progressByKey={activeJobsProgress}
        onSelectNode={selectNode}
        batchBadges={batchBadges}
      />
      {/* S246/S248: la pareja, en el PIE del widget y con peso secundario. Es la MISMA barra del
          maestro-detalle: una sola pareja, la del lote del job mostrado, y ninguna si no tiene lote. */}
      <BatchActionsBar
        group={selectedBatchGroup}
        badge={selectedBatchGroup ? buildBatchBadges(batchActionGroups, t_)[selectedBatchGroup.batchId] : null}
        onPauseBatch={pauseBatchGroup}
        onOpenFinishBatch={(batchId) => setBatchFinish({ open: true, batchId })}
        t_={t_}
      />
      {/* S147: el MISMO renderizador del BLOCKED que usa el maestro-detalle, nunca un segundo. */}
      <BatchBlockedNotice open={blockedPause.open} jobs={blockedPause.jobs} onClose={closeBlockedPause} />
    </Box>
  )
}

const ActiveFabricationOrderJobHeader = (props) => {
  const { globalData, setGlobalData, fabricationOrder, jobReference } = useMosInfo()
  const { t_ } = useSmartTranslation()
  const showJobsList = globalData?.showActiveJobsList === true
  if (globalData.activeJobQuantity > 1 && fabricationOrder && jobReference && !showJobsList) {
    return (
      <Button
        variant='outlined'
        size='small'
        onClick={() => setGlobalData(prev => ({ ...prev, showActiveJobsList: true }))}
        sx={{ borderRadius: 1, textTransform: 'none', minHeight: 32 }}
      >
        {t_('Back to list')}
      </Button>
    )
  }
  return null
}

class ActiveFabricationOrderJobWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'ActiveFoJobListWidget'
  getName = () => 'Active jobs'
  getIcon = () => CropRotateIcon
  createWidget = (props) => <ActiveFabricationOrderJobWidget {...props} />
  getCustomHeader = (props) => <><Box sx={{ flexGrow: 1 }} /><ActiveFabricationOrderJobHeader {...props} /></>
  size = { defaultW: 2, defaultH: 6, minW: 2, minH: 6, maxW: 2, maxH: 6 }
  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export { ActiveFabricationOrderJobWidget }
export default ActiveFabricationOrderJobWidgetProvider
